// Copyright (c) Ping. All rights reserved.
//
// Web providers for the Share-location modal: device geolocation +
// forward/reverse geocoding. These are the `onResolveCurrentLocation` /
// `onSearch` callbacks the shared `<ShareLocationModal>` consumes so the
// "Current location" tab, the open-with-current-location default, and the
// search results all work.
//
// Geocoding uses OpenStreetMap Nominatim — keyless, fine for development
// and light use. For production, point these at your own geocoder (or a
// keyed provider) to respect rate limits / usage policy.
//
// PRIVACY (Hard rule 3): coordinates are sensitive — they are NEVER passed
// to a logger here. They only leave the device as the geocoding request
// the user explicitly triggered (current-location / search).

import type { Location, LocationSearchResult } from '@ping/ui';

const NOMINATIM = 'https://nominatim.openstreetmap.org';

interface NominatimHit {
  readonly place_id: number;
  readonly display_name: string;
  readonly name?: string;
  readonly lat: string;
  readonly lon: string;
}

/** Forward-geocode a free-text query → ranked place results. */
export async function searchPlaces(
  query: string,
  signal: AbortSignal,
): Promise<readonly LocationSearchResult[]> {
  const url =
    `${NOMINATIM}/search?format=jsonv2&addressdetails=0&limit=6` +
    `&q=${encodeURIComponent(query)}`;
  const res = await fetch(url, {
    signal,
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) return [];
  const hits = (await res.json()) as NominatimHit[];
  return hits.map((h) => {
    const lat = Number.parseFloat(h.lat);
    const lng = Number.parseFloat(h.lon);
    // Split the display name into a short label (first segment) + the
    // remaining address, matching the two-line result-row design.
    const parts = h.display_name.split(',');
    const label = (h.name && h.name.length > 0 ? h.name : parts[0] ?? '').trim();
    const address = parts.slice(1).join(',').trim();
    return {
      id: String(h.place_id),
      label: label.length > 0 ? label : h.display_name,
      ...(address.length > 0 ? { address } : {}),
      location: { lat, lng, label },
    } satisfies LocationSearchResult;
  });
}

/** Reverse-geocode a point → a human label/address (best-effort). */
async function reverseGeocode(
  lat: number,
  lng: number,
  signal: AbortSignal,
): Promise<{ label?: string; address?: string }> {
  try {
    const url = `${NOMINATIM}/reverse?format=jsonv2&lat=${lat}&lon=${lng}`;
    const res = await fetch(url, { signal, headers: { Accept: 'application/json' } });
    if (!res.ok) return {};
    const hit = (await res.json()) as NominatimHit;
    const parts = (hit.display_name ?? '').split(',');
    return {
      label: (hit.name && hit.name.length > 0 ? hit.name : parts[0] ?? '').trim(),
      address: parts.slice(1).join(',').trim(),
    };
  } catch {
    return {};
  }
}

/** Resolve the device's current location via the browser geolocation API. */
export async function resolveCurrentLocation(
  signal: AbortSignal,
): Promise<Location | null> {
  if (typeof navigator === 'undefined' || navigator.geolocation === undefined) {
    return null;
  }
  const pos = await new Promise<GeolocationPosition | null>((resolve) => {
    const onAbort = (): void => resolve(null);
    signal.addEventListener('abort', onAbort, { once: true });
    navigator.geolocation.getCurrentPosition(
      (p) => {
        signal.removeEventListener('abort', onAbort);
        resolve(p);
      },
      () => {
        signal.removeEventListener('abort', onAbort);
        resolve(null);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
    );
  });
  if (pos === null || signal.aborted) return null;
  const { latitude, longitude } = pos.coords;
  const { label, address } = await reverseGeocode(latitude, longitude, signal);
  return {
    lat: latitude,
    lng: longitude,
    label: label && label.length > 0 ? label : 'Current location',
    ...(address && address.length > 0 ? { address } : {}),
  };
}
